// A register's text filter: it searches as you type. Born as the deal register's counterparty
// filter; the counterparty register's name filter is the same control with another label.
//
// It was uncontrolled, with `defaultValue` and `onBlur`, so nothing happened until focus left the
// field. Typing a name and looking at the table showed the unfiltered register, and the filter only
// fired on a tab or a click elsewhere - which reads as a broken filter rather than a deliberate one.
//
// Two things follow from the register keeping its whole state in the URL, and both have to be
// handled here or the field is worse than the one it replaces.
//
// The input must be controlled and kept in step with the URL: the back button, a shared link and a
// filter reset all change the value from outside the field, and an uncontrolled input would go on
// showing the old text. But it cannot simply mirror the URL either, because our own commit changes
// the URL mid-typing and would then clobber the characters typed since. So the URL is adopted only
// when it differs from what this component last committed - that is what distinguishes "someone
// pressed back" from "our own write landed".
//
// And the write replaces rather than pushes once a filter is already in the URL, so typing an
// eight-character name leaves one history entry instead of eight. The first write of a filter still
// pushes, which is what makes the back button return to the unfiltered register rather than
// leaving it.

import { useEffect, useRef, useState } from 'react';

const DELAY_MS = 1000;

export default function SearchFilter({ label, placeholder, value, onCommit }) {
  const committed = value ?? '';
  const [draft, setDraft] = useState(committed);

  // What this component last sent upwards. State, not a ref, and deliberately a dependency of the
  // effect below: the URL is adopted when it differs from what we last wrote, and both halves of
  // that comparison have to be able to wake the effect. With only the URL as a dependency, React
  // may never render the intermediate value - our own write lands and the back button follows
  // within one batch, so the URL goes from empty to empty across two renders while the last write
  // says "Vitol" - and the field kept the name over an unfiltered register.
  const [lastWritten, setLastWritten] = useState(committed);
  const timer = useRef(null);

  useEffect(() => {
    if (committed !== lastWritten) {
      setLastWritten(committed);
      setDraft(committed);
    }
  }, [committed, lastWritten]);

  // A pending write must not land after the register has gone; the cleanup is the whole reason this
  // is an effect rather than a bare timeout in the handler.
  useEffect(() => () => clearTimeout(timer.current), []);

  const onChange = (next) => {
    setDraft(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLastWritten(next);
      // Replace only once a filter is already in the URL - see the note at the top. `committed` is
      // the URL's value, not the one being written, which is what makes the first write of a burst
      // a push and every refinement of it a replace.
      onCommit(next, { replace: Boolean(committed) });
    }, DELAY_MS);
  };

  return (
    <label>
      {label}
      <input
        type="search"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
